import { useState } from "react";
import { Button, DeleteButton } from "./Button";
import { tMsg } from "./ToastNotifications";
import _ from "lodash";
import apiAgent from "../api/apiAgent";
import { Spinner } from "./Loader";
import { useAtomValue } from "jotai";
import { hostDataAtom } from "../atoms";
import { singleItemsConfig } from "../utils/singleItemsConfig";
import { ConfirmationPopup } from "./ConfirmationPopup";

const fieldList = [
  "date",
  "date_optional",
  "description",
  "hidden",
  "marked_as_new",
  "place",
  "promoted",
  "thumb_title_optional",
  "thumbnail",
  "title",
  "topics",
];

interface ITags {
  place: string[];
  topics: string[];
}

export const CustomDataImport = ({
  modelName,
}: {
  modelName: "moment" | "landmark";
}) => {
  const [data, setData] = useState<any[] | null>(null);
  const [parsingText, setParsingText] = useState<boolean>(false);
  const [pushingData, setPushingData] = useState<boolean>(false);
  const hostData = useAtomValue(hostDataAtom);
  console.log(hostData);

  const baseUrl = `https://${hostData.aws_bucket}.s3.${hostData.aws_region}.amazonaws.com/`;
  const changeFileUrl = (original: string | undefined): string => {
    if (original && original.length > 0)
      return `${baseUrl}${original.split("/").pop()}`;
    else return "";
  };

  const onFileSelected = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setParsingText(true);
        try {
          const result = (e.target?.result || "").toString();
          if (result.length > 0) {
            const parsed = JSON.parse(result);
            console.log("file data:", parsed.moments);
            setData(parsed.moments);
          } else setData(null);
        } finally {
          setParsingText(false);
        }
      };
      reader.readAsText(file);
    }
  };

  const pushData = async () => {
    try {
      setPushingData(true);
      if (!data) throw "Invalid data";

      // Get any existing moments to for sort_id length
      const getCurrentMoments = await apiAgent.DataType.getAll(modelName);
      const sort_id = getCurrentMoments?.data?.length || 999;

      // Get any existing places
      const getPlaces = await apiAgent.SingleItem.get("place");
      const currentPlaces: string[] =
        JSON.parse(getPlaces.data.data).values || [];

      // Get any existing topics
      const getTopics = await apiAgent.SingleItem.get("topics");
      const currentTopics: string[] =
        JSON.parse(getTopics.data.data).values || [];

      // Parse data from file to select only fillable fields
      const parsedMoments = data.map((item) => {
        return {
          ..._.pick(item, fieldList),
          thumbnail: changeFileUrl(item.thumbnail || ""),
          sort_id,
        };
      });

      // Parse data from file to pull unique tags
      const allTags: ITags = parsedMoments.reduce(
        (obj: ITags, item: any) => {
          return {
            place: _.uniq([...obj.place, ...item.place]),
            topics: _.uniq([...obj.topics, ...item.topics]),
          };
        },
        { place: currentPlaces, topics: currentTopics }
      );
      console.log("Adding these tags", allTags);

      // Add tags
      const addPlacesResponse = await apiAgent.SingleItem.update(
        "place",
        { values: allTags.place },
        JSON.stringify(singleItemsConfig.place.schema)
      );

      const addTopicsResponse = await apiAgent.SingleItem.update(
        "topics",
        { values: allTags.topics },
        JSON.stringify(singleItemsConfig.topics.schema)
      );
      if (
        addTopicsResponse.status === 200 &&
        addPlacesResponse.status === 200
      ) {
        tMsg.success(`Updated all tags.`);
        console.log("Added tag data:", allTags);
      }

      // Add all moments
      const addMomentsResponse = await apiAgent.DataType.create(modelName, {
        item_rows: parsedMoments,
      });
      if (addMomentsResponse.status === 200) {
        tMsg.success(`Added ${parsedMoments.length} ${modelName}s!`);
        console.log(`Added ${modelName} data:`, parsedMoments);
      } else throw `Could not create ${modelName}s`;

      // Get all media based on new moments
      const newMomentsResponse = await apiAgent.DataType.getAll(modelName);
      const newMoments = newMomentsResponse.data;

      // - Reduce media from original data
      const mediaToBeInserted = data.reduce((arr: any[], item) => {
        if (item.media.length < 1) {
          console.log(`No media in ${item.date} - ${item.title}`);
          return arr;
        }
        // match to moment
        const matchedItem = newMoments.find(
          (nm: any) => nm.title === item.title && nm.date === item.date
        );
        if (!matchedItem) {
          console.log(`No matching item for ${item.date} - ${item.title}`);
          return arr;
        }

        const owner_id = matchedItem.id;
        const owner_key = `owner_${modelName}_id`;

        // parse media and file urls
        const parsedMedia = item.media.map((item: any) => {
          const isVideo = item.media_type === "video";

          const image: string = !isVideo
            ? changeFileUrl(item.media_file.url)
            : "";
          const video: string = isVideo
            ? changeFileUrl(item.media_file.url)
            : "";
          const subtitles: string = isVideo
            ? changeFileUrl(item.subtitles.url)
            : "";
          const media_title_image: string = changeFileUrl(
            item.media_title_image.url || ""
          );

          return {
            [owner_key]: owner_id,
            caption: item.caption,
            credit: item.credit,
            object_location: item.object_location,
            media_type: item.media_type || "image",
            silent_video: item.silent_video || false,
            media_title_image,
            image,
            video,
            subtitles,
          };
        });

        return [...arr, ...parsedMedia];
      }, []);

      // Add all media
      console.log("Adding media data:", mediaToBeInserted);
      const addMediaResponse = await apiAgent.DataType.create("media", {
        item_rows: mediaToBeInserted,
      });
      if (addMediaResponse.status === 200) {
        tMsg.success(`Added ${mediaToBeInserted.length} media!`);
      } else throw "Could not create moments";
    } catch (err) {
      tMsg.error("Error pushing data - see console for details");
      console.log(err);
    } finally {
      setPushingData(false);
    }
  };

  const printData = () => {
    if (!data) return;
    const parsed = data.map((item: any) => {
      const preFields = _.pick(item, [
        "id",
        "date",
        "date_optional",
        "title",
        "description",
        "thumbnail",
      ]);
      const postFields = _.pick(item, ["hidden", "marked_as_new", "promoted"]);
      return {
        ...preFields,
        media: item.media.reduce((str: string, m: any, index: number) => {
          if (index === 0) return `${m.id}`;
          return `${str},${m.id}`;
        }, ""),
        place: item.place.reduce((str: string, obj: any, index: number) => {
          if (index === 0) return `${obj}`;
          return `${str},${obj}`;
        }, ""),
        topics: item.topics.reduce((str: string, obj: any, index: number) => {
          if (index === 0) return `${obj}`;
          return `${str},${obj}`;
        }, ""),
        ...postFields,
      };
    });
    console.log(JSON.stringify({ moments: parsed }));
  };

  return (
    <div className="text-xs border py-1.5 flex gap-4">
      <div className="space-y-1">
        <div className="font-bold">Custom Data Import</div>
        <input
          type="file"
          onChange={(e) =>
            onFileSelected(e.target.files ? e.target.files[0] : null)
          }
          accept=".txt,.json"
          disabled={pushingData}
        />
      </div>
      <Button
        className="px-4 grid content-center"
        disabled={!data || parsingText || pushingData}
        onClick={(e) => {
          e.preventDefault();
          printData();
        }}
      >
        Print Data to Console
      </Button>
      <Button
        className="px-4 grid content-center"
        disabled={!data || parsingText || pushingData}
        onClick={(e) => {
          e.preventDefault();
          pushData();
        }}
      >
        {pushingData ? <Spinner /> : `Push data`}
      </Button>
    </div>
  );
};

export const ClearMomentsButton = ({
  modelName,
}: {
  modelName: "moment" | "landmark";
}) => {
  const [clearing, setClearing] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const clearItems = async () => {
    try {
      setClearing(true);
      const getAllItems = await apiAgent.DataType.getAll(modelName);
      const target_ids = getAllItems.data?.map((item: any) => item.id) || [];
      const deleteItemsResponse = await apiAgent.DataType.delete({
        model_name: modelName,
        target_ids,
      });

      if (deleteItemsResponse.status === 200) {
        tMsg.success(`Cleared all ${modelName} items`);
      }

      const getAllMedia = await apiAgent.DataType.getAll("media");
      const ownerKey = `owner_${modelName}_id`;
      const ids_of_related_media =
        getAllMedia.data?.reduce((arr: any[], item: any) => {
          if (item[ownerKey]) return [...arr, item.id];
          return arr;
        }, []) || [];

      const deleteMediaResponse = await apiAgent.DataType.delete({
        model_name: "media",
        target_ids: ids_of_related_media,
      });

      if (deleteMediaResponse.status === 200) {
        tMsg.success(`Cleared all media related to ${modelName} items`);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setClearing(false);
      setShowPopup(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowPopup(true)}
        disabled={clearing}
        className="grid content-center text-xs bg-red-500 border-red-500"
      >
        {`Clear ${modelName} items`}
      </Button>
      <ConfirmationPopup
        confirmAction={() => clearItems()}
        showModal={showPopup}
        setShowModal={setShowPopup}
        ActionButton={DeleteButton}
      >
        <div className="font-bold text-2xl text-white bg-red-500 text-center w-full">
          WARNING
        </div>
        <div className="font-bold mt-2 text-base">{`You are about to delete ALL ${modelName} items`}</div>
      </ConfirmationPopup>
    </>
  );
};
